import { CloudUpload, Description, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useState, useCallback } from 'react';

import { BulkPaymentValidation, ParsedPaymentRow } from '@/types/payment';

interface BulkUploadProps {
  onFileProcessed: (validation: BulkPaymentValidation) => void;
}

export const BulkUpload: React.FC<BulkUploadProps> = ({ onFileProcessed }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');

  const validatePaymentRow = (row: any, index: number): ParsedPaymentRow => {
    const errors: string[] = [];

    // Validate nombre_beneficiario (support both old and new field names)
    const beneficiario = row.nombre_beneficiario || row.beneficiario || '';
    if (!beneficiario || typeof beneficiario !== 'string' || !beneficiario.trim()) {
      errors.push('Nombre del beneficiario requerido');
    }

    // Validate email (optional)
    const email = String(row.email || '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push('Email inválido');
    }

    // Validate CLABE
    const clabe = String(row.clabe || '').replace(/\s/g, '');
    if (!clabe) {
      errors.push('CLABE requerido');
    } else if (clabe.length !== 18 || !/^\d{18}$/.test(clabe)) {
      errors.push('CLABE debe tener 18 dígitos');
    }

    // Validate RFC (optional)
    const rfc = String(row.rfc || '')
      .toUpperCase()
      .trim();
    if (rfc && !/^[A-Z&Ñ]{3,4}\d{6}[A-Z\d]{3}$/.test(rfc)) {
      errors.push('RFC inválido');
    }

    // Validate monto
    const monto = parseFloat(String(row.monto || '').replace(/,/g, ''));
    if (isNaN(monto) || monto <= 0) {
      errors.push('Monto debe ser mayor a 0');
    }

    // Validate concepto
    if (!row.concepto || typeof row.concepto !== 'string' || !row.concepto.trim()) {
      errors.push('Concepto requerido');
    }

    // Validate fecha (optional) - preserve original format with time
    const fechaStr = String(row.fecha || '').trim();
    let scheduledDate: string | undefined = undefined;

    if (fechaStr) {
      // Support both YYYY-MM-DD and YYYY-MM-DD HH:MM formats
      const fechaRegex = /^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}(:\d{2})?)?$/;
      if (!fechaRegex.test(fechaStr)) {
        errors.push('Fecha inválida (formato: YYYY-MM-DD o YYYY-MM-DD HH:MM)');
      } else {
        // Keep the original format string without timezone conversion
        scheduledDate = fechaStr;
      }
    }

    return {
      id: `row-${index}`,
      row: index + 1,
      beneficiario: String(beneficiario || '').trim(),
      email: email || undefined,
      clabe,
      rfc: rfc || undefined,
      monto: isNaN(monto) ? 0 : monto,
      concepto: String(row.concepto || '').trim(),
      fecha: scheduledDate || undefined,
      isValid: errors.length === 0,
      errors,
    };
  };

  const processCSVFile = async (file: File): Promise<BulkPaymentValidation> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        try {
          const text = e.target?.result as string;
          const lines = text.trim().split('\n');

          if (lines.length < 2) {
            reject(
              new Error('El archivo debe contener al menos una fila de datos además del encabezado')
            );
            return;
          }

          // Parse header
          const headers = lines[0]?.split(',').map(h => h.trim().toLowerCase());
          const requiredHeaders = ['clabe', 'monto', 'concepto'];
          // Support both old 'beneficiario' and new 'nombre_beneficiario'
          const hasBeneficiario =
            headers?.includes('beneficiario') || headers?.includes('nombre_beneficiario');

          if (!hasBeneficiario) {
            reject(new Error('Columna faltante: nombre_beneficiario'));
            return;
          }

          const missingHeaders = requiredHeaders.filter(h => !headers?.includes(h));
          if (missingHeaders.length > 0) {
            reject(new Error(`Columnas faltantes: ${missingHeaders.join(', ')}`));
            return;
          }

          // Parse data rows
          const dataRows = lines.slice(1);
          const payments: ParsedPaymentRow[] = [];

          dataRows.forEach((line, index) => {
            if (line.trim()) {
              const values = line.split(',').map(v => v.trim());
              const rowData: any = {};

              headers?.forEach((header, i) => {
                rowData[header] = values[i] || '';
              });

              const payment = validatePaymentRow(rowData, index);
              payments.push(payment);
            }
          });

          const validPayments = payments.filter(p => p.isValid);
          const totalAmount = validPayments.reduce((sum, p) => sum + p.monto, 0);
          const commission = totalAmount * 0.0015; // 0.15%

          const validation: BulkPaymentValidation = {
            totalRows: payments.length,
            validRows: validPayments.length,
            invalidRows: payments.length - validPayments.length,
            payments,
            batchSummary: {
              totalAmount,
              totalCommission: commission,
              totalWithCommission: totalAmount + commission,
            },
          };

          resolve(validation);
        } catch (error) {
          reject(error as Error);
        }
      };

      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsText(file);
    });
  };

  const handleFileSelect = useCallback(
    async (file: File) => {
      setError('');
      setSelectedFile(file);
      setIsProcessing(true);

      try {
        // Validate file type
        if (!file.name.toLowerCase().endsWith('.csv')) {
          throw new Error('Solo se permiten archivos .csv');
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('El archivo es demasiado grande (máximo 10MB)');
        }

        const validation = await processCSVFile(file);
        onFileProcessed(validation);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al procesar el archivo');
        setSelectedFile(null);
      } finally {
        setIsProcessing(false);
      }
    },
    [onFileProcessed]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        if (!files[0]) return;
        void handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (!files[0]) return;
      void handleFileSelect(files[0]);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-brand-white mb-2">Subir Archivo</h2>
        <p className="text-muted">Sube tu archivo Excel (.xlsx) o CSV (.csv) con los pagos</p>
      </div>

      {/* File Upload Area */}
      <CardContainer className="mb-8">
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            isDragOver ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:border-muted'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="space-y-4">
            <div className="w-16 h-16 bg-black/10 dark:bg-white/10 rounded-full flex items-center justify-center mx-auto">
              <CloudUpload className="text-muted" fontSize="large" />
            </div>

            <div>
              <h3 className="text-lg font-medium text-brand-white mb-2">Seleccionar Archivo</h3>
              <p className="text-muted mb-4">Archivos soportados: .xlsx, .csv (máximo 10MB)</p>

              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileInput}
                className="hidden"
                id="file-input"
                disabled={isProcessing}
              />

              <Button
                variant="black"
                size="large"
                onClick={() => document.getElementById('file-input')?.click()}
                disabled={isProcessing}
                className="mb-2"
              >
                {isProcessing ? 'Procesando...' : 'Seleccionar Archivo'}
              </Button>

              <p className="text-sm text-muted">o arrastra y suelta el archivo aquí</p>
            </div>
          </div>
        </div>

        {/* Selected File Info */}
        {selectedFile && (
          <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <CheckCircle className="text-green-400" />
              <div className="flex-1">
                <div className="font-medium text-brand-white">Archivo seleccionado</div>
                <div className="text-sm text-muted">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </div>
              </div>
              <Description className="text-green-400" />
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <ErrorIcon className="text-red-400" />
              <div className="flex-1">
                <div className="font-medium text-brand-white">Error al procesar archivo</div>
                <div className="text-sm text-muted">{error}</div>
              </div>
            </div>
          </div>
        )}
      </CardContainer>

      {/* Instructions */}
      <CardContainer className="p-6">
        <h4 className="text-lg font-semibold text-brand-white mb-4">Instrucciones</h4>
        <ul className="space-y-2 text-sm text-muted">
          <li className="flex items-start space-x-2">
            <span className="text-brand-blue mt-1">•</span>
            <span>
              Asegúrate de que tu archivo contenga todas las columnas obligatorias
              (nombre_beneficiario, clabe, monto, concepto)
            </span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-blue mt-1">•</span>
            <span>El email es opcional, pero debe ser válido si se proporciona</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-blue mt-1">•</span>
            <span>Los montos deben estar en formato numérico (usa . para decimales)</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-blue mt-1">•</span>
            <span>Los CLABE deben tener exactamente 18 dígitos</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-blue mt-1">•</span>
            <span>
              RFC y fecha son opcionales. Si incluyes fecha, usa formato YYYY-MM-DD HH:MM para pagos
              programados
            </span>
          </li>
        </ul>
      </CardContainer>
    </div>
  );
};
